import { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import DatePicker from "react-date-picker";
import "react-date-picker/dist/DatePicker.css";
import "react-calendar/dist/Calendar.css";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useProfileStore } from "../../store/useUserStore";

type Props = {};

const StudentEdit = (props: Props) => {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const [innerLoading, setInnerLoading] = useState(false);
  const { t } = useTranslation();
  const {
    fetchStudents,
    students,
    setLoading,
    setShowGuidanceFlow,
    setGuidanceStep,
  } = useProfileStore((state: any) => state);

  const [studentData, setStudentData] = useState<any>({
    name: "",
    // email: "",
    ic: "",
    birthday: new Date(),
    relationship: "",
    school: "",
    profile_pic: "/assets/img/default-profile.jpg",
    student_standard: "",
    // school_classroom: "",
    school_name: "",
    nationality: "",
  });
  const [standardOptions, setStandardOptions] = useState<any[] | null>(null);
  const hasLoadedStudentDetail = useRef(false);

  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [classroomsLoading, setClassroomsLoading] = useState(false);
  const classroomsTimer = useRef<number | null>(null);

  const fetchClassrooms = async (school_code: string) => {
    try {
      setClassroomsLoading(true);
      const res = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/student/class?school_code=${school_code}`,
        {
          headers: {
            Authorization: localStorage.getItem("parent_token"),
            "Content-Type": "application/json",
          },
        },
      );
      setClassrooms(res.data?.result?.data ?? []);
    } catch (err) {
      setClassrooms([]);
      toast.error(t("server_error"));
    } finally {
      setClassroomsLoading(false);
    }
  };

  useEffect(() => {
    getStandardOptions();
    if (type !== "edit" && type !== "add") {
      navigate(`/`);
    }
  }, []);

  useEffect(() => {
    const code = studentData.school?.toString().trim();
    if (type === "edit" && code && code.length >= 5) {
      fetchClassrooms(code);
    } else {
      setClassrooms([]);
    }
  }, [type, studentData.school]);

  useEffect(() => {
    // Only fetch student detail after standardOptions are loaded as an array
    if (
      type === "edit" &&
      Array.isArray(standardOptions) &&
      standardOptions.length > 0 &&
      !hasLoadedStudentDetail.current
    ) {
      hasLoadedStudentDetail.current = true;
      getStudentDetail();
    }
  }, [type, standardOptions]);

  const getStudentDetail = async () => {
    setLoading(true);
    api
      .get(`${process.env.REACT_APP_API_BASE_URL}/student/${id}`, {
        headers: {
          Authorization: localStorage.getItem("parent_token"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        console.log("res studentDetails", res.data.data);
        setStudentData({
          name: res.data.data.name,
          ic: res.data.data.ic_number,
          birthday: res.data.data.birthday,
          relationship: res.data.data.relationship,
          school: res.data.data.school ? res.data.data.school.school_id : "",
          profile_pic:
            res.data.data.profile_pic || "/assets/img/default-profile.jpg",
          student_standard: res.data.data.student_standard
            ? getStandardIdByName(res.data.data.student_standard)
            : "",
          school_classroom: res.data.data.school_classroom,
          school_name: res.data.data.school_name || "",
          nationality: res.data.data.nationality || "",
        });

        // const sortedAttendance = [...res.data.data.attendance].sort((a, b) => new Date(b.course_date).getTime() - new Date(a.course_date).getTime())
        // setAttendances(sortedAttendance)
      })
      .catch((err) => {
        setLoading(false);
        console.log("err", err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const getStandardIdByName = (name: string) => {
    if (!Array.isArray(standardOptions)) return "";
    const standard: any = standardOptions.find(
      (item: any) => item.name == name,
    );
    return standard ? standard.id : "";
  };

  const getStandardOptions = async () => {
    // setLoading(true)
    api
      .get(`${process.env.REACT_APP_API_BASE_URL}/studentstandard`, {
        headers: {
          Authorization: localStorage.getItem("parent_token"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        console.log("res standard", res);
        setStandardOptions(res.data.result.data);
      })
      .catch((err) => {
        console.log("err", err);
        toast.error(t("server_error"));
      })
      .finally(() => {
        // setLoading(false)
      });
  };

  const updateStudent = async (values: any) => {
    const date = new Date(values.birthday);
    const formattedDate = date.toLocaleDateString("en-CA");
    let payload: any = {
      name: values.name,
      // "email": studentData.email.trim(),
      ic_number: values.ic,
      birthday: formattedDate,
      relationship: values.relationship,
      student_standard: parseInt(values.student_standard),
      school_classroom: values.school_classroom,
      school_id: values.school,
    };

    // Check if profile_pic is Base64 data
    if (studentData.profile_pic.startsWith("data:")) {
      payload["profile_pic"] = studentData.profile_pic;
    }

    if (values.school == "") {
      delete payload["school_id"];
    }

    if (studentData.nationality && !values.nationality) {
      payload.nationality = null;
    } else if (values.nationality) {
      payload.nationality = values.nationality;
    }

    if (studentData.school_name && !values.school_name) {
      payload.school_name = null;
    } else if (values.school_name) {
      payload.school_name = values.school_name;
    }

    setInnerLoading(true);
    api
      .put(
        `${process.env.REACT_APP_API_BASE_URL}/student/edit/${id}`,
        payload,
        {
          headers: {
            Authorization: localStorage.getItem("parent_token"),
            "Content-Type": "application/json",
          },
        },
      )
      .then((res) => {
        setInnerLoading(false);
        console.log("res", res.data);
        navigate("/profile");
        toast.success(t("success"));
      })
      .catch((err) => {
        setInnerLoading(false);
        console.log("err", err);

        if (err.response && err.response.data.error) {
          toast.error(err.response.data.error);
        } else if (
          err.response &&
          err.response.data &&
          err.response.data.errors
        ) {
          for (let msgKey in err.response.data.errors) {
            toast.error(err.response.data.errors[msgKey][0]);
          }
        }
      })
      .finally(() => {
        fetchStudents();
      });
  };

  const createStudent = async (values: any) => {
    const date = new Date(values.birthday);
    const formattedDate = date.toLocaleDateString("en-CA");
    let payload: any = {
      name: values.name,
      // "email": studentData.email,
      ic_number: values.ic,
      birthday: formattedDate,
      // "relationship": studentData.relationship,
      // profile_pic: studentData.profile_pic,
      student_standard: values.student_standard,
      school_classroom: values.school_classroom,
      school_id: values.school,
    };

    if (values.school == "") {
      delete payload["school_id"];
    }

    if (studentData.profile_pic != "/assets/img/default-profile.jpg") {
      payload["profile_pic"] = studentData.profile_pic;
    }

    if (values.nationality) payload.nationality = values.nationality;
    if (values.school_name) payload.school_name = values.school_name;

    // console.log("payload", payload)
    setInnerLoading(true);
    api
      .post(`${process.env.REACT_APP_API_BASE_URL}/student/add`, payload, {
        headers: {
          Authorization: localStorage.getItem("parent_token"),
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        setInnerLoading(false);
        console.log("res", res.data);
        toast.success(t("success"));

        // Set guidance flow for academy only if this is the first child (students array was empty)
        if (students.length === 0) {
          setShowGuidanceFlow(true);
          setGuidanceStep("search-academy");
        }

        navigate("/profile");
      })
      .catch((err) => {
        setInnerLoading(false);
        console.log("err", err);

        if (err.response && err.response.data.error) {
          toast.error(err.response.data.error);
        } else if (
          err.response &&
          err.response.data &&
          err.response.data.errors
        ) {
          for (let msgKey in err.response.data.errors) {
            toast.error(`${msgKey}: ${err.response.data.errors[msgKey][0]}`);
          }
        }
      })
      .finally(() => {
        fetchStudents();
      });
  };

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      const validExtensions = ["image/jpeg", "image/png", "image/jpg"];
      if (!validExtensions.includes(file.type))
        return toast.error(t("student.edit.only_jpg"));

      const maxFileSize = 3 * 1024 * 1024; // 1 MB in bytes
      if (file.size > maxFileSize)
        return toast.error(t("student.edit.image_size_limit"));

      const reader = new FileReader();
      reader.onloadend = () => {
        setStudentData((prevData: any) => ({
          ...prevData,
          profile_pic: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(t("validation.required")),
    birthday: Yup.date().required(t("validation.required")),
    ic: Yup.string().required(t("validation.required")),
    school: Yup.string(),
    school_name: Yup.string().when("school", {
      is: (val: string) => val && val.trim() !== "",
      then: (schema) => schema.required(t("validation.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    student_standard: Yup.number().when("school", {
      is: (val: string) => val && val.trim() !== "",
      then: (schema) => schema.required(t("validation.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    school_classroom: Yup.string().when("school", {
      is: (val: string) => val && val.trim() !== "",
      then: (schema) => schema.required(t("validation.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    nationality: Yup.string().when("school", {
      is: (val: string) => val && val.trim() !== "",
      then: (schema) => schema.required(t("validation.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return (
    <>
      <div className="pb-20">
        <div className="mb-2 grid grid-cols-4 px-2">
          <div>
            <IoIosArrowBack
              size={28}
              color="#000"
              onClick={() => navigate("/profile")}
            />
          </div>
          <div className="text-center col-span-2 text-lg font-medium">{`${
            type == "add"
              ? t("student.edit.add_child")
              : t("student.edit.edit_child")
          }`}</div>
          <div></div>
        </div>

        <div className="mt-7">
          <div className="flex flex-col items-center">
            <div
              className="relative"
              style={{
                height: "120px",
                width: "8rem",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={studentData.profile_pic || "/default-profile.png"}
                className="img-fluid"
                alt={t("student.edit.profile_pic")}
                style={{
                  height: "100%",
                  width: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                }}
              />
              <button
                className="absolute bottom-1 right-1 bg-theme text-white rounded-full px-2.5 py-0.5 shadow-md"
                onClick={() => document.getElementById("fileInput")!.click()}
              >
                {" "}
                +{" "}
              </button>
              <input
                id="fileInput"
                type="file"
                accept=".jpg,.jpeg,.png"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <p className="name">{t("student.edit.upload_picture")}</p>
            </div>
          </div>

          <Formik
            enableReinitialize
            initialValues={{
              name: studentData.name || "",
              birthday: studentData.birthday
                ? new Date(studentData.birthday)
                : "",
              school_classroom: studentData.school_classroom || "",
              student_standard: studentData.student_standard,
              school: studentData.school || "",
              ic: studentData.ic || "",
              school_name: studentData.school_name || "",
              nationality: studentData.nationality || "",
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              type === "add" ? createStudent(values) : updateStudent(values);
            }}
          >
            {({ setFieldValue, values }) => (
              <Form className="py-4 px-3 shadow-lg rounded-2xl">
                <div className="mb-3">
                  <label className="text-md font-medium mb-2">
                    {t("student.edit.full_name")}
                  </label>
                  <div className="flex items-center gap-2">
                    <Field
                      name="name"
                      type="text"
                      placeholder={t("student.edit.full_name")}
                      className={`flex-grow px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme`}
                    />
                  </div>
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="ml-4 pt-1 text-red-500 text-sm"
                  />
                </div>

                <>
                  <div className="mb-3">
                    <label className="text-md font-medium mb-2">
                      {t("student.edit.birthday")}
                    </label>
                    <Field name="birthday">
                      {({ field, form }: any) => (
                        <>
                          <DatePicker
                            onChange={(date) =>
                              form.setFieldValue("birthday", date)
                            }
                            value={field.value ? new Date(field.value) : null}
                            format="yyyy-MM-dd"
                            className="min-w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                          />
                          <ErrorMessage
                            name="birthday"
                            component="div"
                            className="ml-4 pt-1 text-red-500 text-sm"
                          />
                        </>
                      )}
                    </Field>
                  </div>

                  <div className="mb-3">
                    <label className="text-md font-medium mb-2">
                      {t("student.edit.school_name")}{" "}
                    </label>
                    <Field
                      name="school_name"
                      type="text"
                      placeholder={t("student.edit.school_name")}
                      className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                    />
                    <ErrorMessage
                      name="school_name"
                      component="div"
                      className="ml-4 pt-1 text-red-500 text-sm"
                    />
                  </div>

                  <div className="relative mb-3">
                    <label className="text-md font-medium mb-2">
                      {t("student.edit.standard")}
                    </label>
                    <Field
                      as="select"
                      name="student_standard"
                      className="w-full px-4 py-3 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none"
                    >
                      <option value="" disabled>
                        {t("student.edit.select_standard")}
                      </option>
                      {Array.isArray(standardOptions) &&
                        standardOptions.map(
                          (opt: { id: number; name: string }) => (
                            <option value={opt.id} key={opt.id}>
                              {opt.name}
                            </option>
                          ),
                        )}
                    </Field>
                    <ErrorMessage
                      name="student_standard"
                      component="div"
                      className="ml-4 pt-1 text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="text-md font-medium mb-2">
                      {t("student.edit.school_id")}{" "}
                    </label>
                    <Field name="school">
                      {({ field, form }: any) => (
                        <input
                          {...field}
                          type="text"
                          placeholder={t("student.edit.school_id")}
                          className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                          onChange={(e) => {
                            const value = e.target.value;
                            form.setFieldValue("school", value);
                            // Clear previous timer
                            if (classroomsTimer.current) {
                              window.clearTimeout(classroomsTimer.current);
                              classroomsTimer.current = null;
                            }
                            // If input length < 5, clear classrooms and classroom selection
                            if (value.trim().length < 5) {
                              setClassrooms([]);
                              form.setFieldValue("school_classroom", "");
                              return;
                            }
                            // Debounce fetch by 400ms
                            classroomsTimer.current = window.setTimeout(() => {
                              fetchClassrooms(value.trim());
                            }, 400);
                          }}
                        />
                      )}
                    </Field>

                    <ErrorMessage
                      name="school"
                      component="div"
                      className="ml-4 pt-1 text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="text-md font-medium mb-2">
                      {t("student.edit.classroom")}
                    </label>
                    {classroomsLoading ? (
                      <div className="w-full px-4 py-3 border rounded-full text-md">
                        <FaSpinner className="animate-spin inline mr-2" />
                        {t("loading")}
                      </div>
                    ) : classrooms.length > 0 ? (
                      <Field
                        as="select"
                        name="school_classroom"
                        className="w-full px-4 py-3 border rounded-full text-md bg-white focus:outline-none focus:ring-2 focus:ring-theme appearance-none"
                      >
                        <option value="" disabled>
                          {t("student.edit.classroom")}
                        </option>
                        {Array.isArray(standardOptions) &&
                          classrooms.map((opt: { name: string }) => (
                            <option value={opt.name} key={opt.name}>
                              {opt.name}
                            </option>
                          ))}
                      </Field>
                    ) : (
                      <Field
                        name="school_classroom"
                        type="text"
                        placeholder={t("student.edit.classroom")}
                        className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                      />
                    )}
                    <ErrorMessage
                      name="school_classroom"
                      component="div"
                      className="ml-4 pt-1 text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="text-md font-medium mb-2">
                      {t("student.edit.nationality")}
                    </label>
                    <Field
                      name="nationality"
                      type="text"
                      placeholder={t("student.edit.nationality")}
                      className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                    />
                    <ErrorMessage
                      name="nationality"
                      component="div"
                      className="ml-4 pt-1 text-red-500 text-sm"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="text-md font-medium mb-2">
                      {t("student.edit.ic")}
                    </label>
                    <Field
                      name="ic"
                      type="text"
                      placeholder={t("student.edit.ic")}
                      className="w-full px-4 py-3 border rounded-full text-md focus:outline-none focus:ring-2 focus:ring-theme"
                      onCompositionEnd={(e: any) => {
                        const numericValue = e.target.value.replace(/\D/g, "");
                        setFieldValue("ic", numericValue, true);
                      }}
                    />
                    <ErrorMessage
                      name="ic"
                      component="div"
                      className="ml-4 pt-1 text-red-500 text-sm"
                    />
                  </div>

                  <button
                    type="submit"
                    className="text-white text-[16px] leading-none w-full py-[15px] bg-theme border-none rounded-3xl flex items-center justify-center"
                  >
                    {innerLoading ? (
                      <FaSpinner className="animate-spin" />
                    ) : type === "add" ? (
                      t("student.edit.create")
                    ) : (
                      t("student.edit.update")
                    )}
                  </button>
                </>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </>
  );
};

export default StudentEdit;
